"use client";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DeliveryFormValues,
  type TravelBookingFormValues,
  travelBookingSchema,
} from "../lib/schemas/travel-booking";
import { Button } from "@/components/ui/button";
import { FormProvider } from "react-hook-form";
import { DestinationStep } from "../steps/destination-step";
import { CustomerStep } from "../steps/customer-step";
import { DeliveryStep } from "../steps/delivery-step";
import { CalculationPanel } from "../steps/calculation-panel";
import Stepper from "components/Stepper/newstepper";
import { ProductModal } from "../steps/product-modal";
import { useMutation } from "react-query";
import { adddNewContact, createGenericOrder, OrderPayload } from "../apis";
import { Img, Spinner } from "components";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { products, step1, step2Singal } from "../lib/FormConfigs";
import Navbar from "components/Navbar/Index";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { setNewGenricOrderID } from "redux/features/CreateOrder";
import { useAppDispatch } from "redux/hooks";

export interface GenericOrderLocationState extends Location {
  state: {
    opportunityId?: number;
    contactId?: number;
    name?: string;
    email?: string;
    number?: string;
  }
}


export default function TravelBookingPage() {
  const nevigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [, setNewCustomerID] = useState<string>("");
  const location = useLocation() as GenericOrderLocationState;
  const new_order_type =
    new URLSearchParams(window.location.search).get("type") || "";
  const destinationStep = step1[new_order_type as keyof typeof step1];
  // const customerStep = step2[new_order_type as keyof typeof step2Singal];
  const customerStepSingal =
    step2Singal[new_order_type as keyof typeof step2Singal];
  const productsConfig = products[new_order_type as keyof typeof products];

  // Create a single form instance that will collect all data
  const form = useForm<TravelBookingFormValues>({
    resolver: zodResolver(travelBookingSchema),
    defaultValues: {
      destination: {
        purposeOfTravel: "",
        destination: "",
        dateRange: {
          start_date: "",
          end_date: "",
        },
      },
      customers: [],
      deliveries: [],
    },
    mode: "onChange", // Validate on change for better user experience
  });
  const steps = [
    {
      title: "Destination Details",
      fields: ["destination"],
      component: <DestinationStep config={destinationStep} form={form} />,
    },
    {
      title: "Customer & Product Details",
      fields: ["customers"],
      component: <CustomerStep config={customerStepSingal} form={form} />,
    },
    {
      title: "Delivery",
      fields: ["deliveries"],
      component: <DeliveryStep form={form} />,
      optional: true,
    },
  ];

  const step = steps[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;
  // const isOptionalStep = step.optional;

  // check validatoion button
  const validateCurrentStep = async () => {
    const fieldsToValidate = step.fields as (keyof TravelBookingFormValues)[];

    if (fieldsToValidate.includes("customers")) {
      const result = await form.trigger(fieldsToValidate);
      const customers = form.getValues("customers") || [];
      const firstCustomer = customers[0] || {};

      const hasFullName = !!firstCustomer.fullName?.trim();

      const hasValidProducts =
        Array.isArray(firstCustomer.products) &&
        firstCustomer.products.length > 0 &&
        firstCustomer.products.every(
          (product) =>
            product?.productType?.label &&
            Array.isArray(product.currencies) &&
            product.currencies.length > 0 &&
            product.currencies.every(
              (currency) =>
                typeof currency.amount === "number" &&
                typeof currency.rate === "number"
            )
        );

      let isFirstCustomerValid = hasFullName && hasValidProducts;

      if (!hasFullName) {
        form.setError("customers.0.fullName", {
          type: "manual",
          message: "Full name is required.",
        });
      }

      if (!hasValidProducts) {
        form.setError("customers.0.products", {
          type: "manual",
          message:
            "At least one valid product with amount and rate is required.",
        });
      }

      return result || isFirstCustomerValid;
    }
    const result = await form.trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Show validation errors
      console.log("Validation failed", form.formState.errors);
    }
  };
  const {mutateAsync:addContactMutateAsync, isLoading:loader} = useMutation({
    mutationFn: adddNewContact,
    onSuccess: () => {

    },
    onError: () => {
    },
  })

  const handleNextWithProduct = async () => {
    const customerFields = [
      "customers.0.fullName",
      "customers.0.email",
      "customers.0.mobileNumber",
      "customers.0.pan",
    ];

    // Trigger validation only for those fields
    const results = await Promise.all(
      customerFields.map((field) => form.trigger(field as any))
    );

    const customer = form.getValues("customers")[0];

    // If all validations passed
    if (results.every((result) => result === true)) {
      try {
        if (!customer.customerId) {
          const addContactRes = await addContactMutateAsync({
            name: customer.fullName,
            pan_no: customer.pan as string,
            phone_number: customer.mobileNumber,
            email: customer.email as string,
          });

          form.setValue("customers.0.customerId", addContactRes?.id?.toString());          

          const finalLabel = {
            value: addContactRes?.id ? String(addContactRes?.id) : "",
            label: addContactRes?.full_name,
          };

          setNewCustomerID(addContactRes?.id);
          dispatch(setNewGenricOrderID([finalLabel]));
        } else {
          console.log("Existing customer, skipping addContact API");
          setNewCustomerID(customer.customerId);
        }
        setIsProductModalOpen(true);
      } catch (err) {
        console.error("Error adding contact", err);
      }
    } else {
      console.log("Please complete customer information first");
    }
  };

  useEffect(() => {}, [isProductModalOpen]);
  useEffect(()=>{
    const stateData = location?.state
    if(stateData){
      const { contactId, name, email, number } = stateData;      
      if (contactId) {
        form.setValue("customers.0.customerId", contactId.toString());
      }
      if (name) {
        form.setValue("customers.0.fullName", name);
      }
      if (email) {
        form.setValue("customers.0.email", email);
      }
      if (number) {
        form.setValue("customers.0.mobileNumber", number);
      }
    }
  },[location])

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // const handleSkip = () => {
  //   if (isOptionalStep) {
  //     setCurrentStep((prev) => prev + 1);
  //   }
  // };
  const navigate = useNavigate();

  const {  mutateAsync, isLoading } = useMutation({
    mutationFn: createGenericOrder,
    onSuccess: (data) => {
      const orderId = data?.data?.order_id;
      if (orderId) {
        navigate(`/forex/orderdetails/${orderId}`);
        form.reset();
        setCurrentStep(1);
        setIsSubmitting(false);
        setIsProductModalOpen(false);
      }
    },
    onError: (error) => {
      console.error("Error submitting form:", error);
      setIsSubmitting(false);
    },
  });
  console.log(form.formState.errors);

  const handleSubmit = async (values: TravelBookingFormValues) => {
    // extract all product based on cutomer id
    const productsWithCustomerInfo = () => {
      return values.customers.flatMap((customer) =>
        customer.products.map((product) => ({
          customers_id: Number(customer.customerId) || null,
          forex_type: "Sale" as "Sale" | "Purchase",
          currency_have: "INR",
          quantity: product?.currencies?.[0].amount,
          card_type: product?.cardType?.value as
            | "multi currency"
            | "single currency",
          product_id: Number(product?.productType?.value) as number,
          currency_want: product?.currencies?.[0].currency?.value,
          rate: product?.currencies?.[0].rate as number,
          local_id: product?.local_id as string,
          name: customer.fullName,
          phoneNumber: customer.mobileNumber,
          email: customer.email,
          pan_no: customer.pan,
          country: values?.destination?.destination
          // Add any other customer fields you need
        }))
      );
    };
    const transformDeliveriesToDeliveryDetails = (
      deliveries: DeliveryFormValues[]
    ): OrderPayload["delivery_details"] => {
      const deliveryDetails = deliveries.flatMap((delivery) => {
        return delivery.products.map((product) => ({
          local_id: [product.value],
          customers_id: Number(delivery.customers.value),
          address_id: "", // assuming this is used as address_id (adjust if needed)
          delivery_mode: delivery.deliveryMode.value,
          delivery_id: delivery.deliveryId,
          delivery_channel: delivery.deliveryType as string,
          address_line_1: delivery.addresses[0]?.address1 || "",
          address_line_2: delivery.addresses[0]?.address2 || "",
          city: delivery.addresses[0]?.city || "",
          state: delivery.addresses[0]?.state?.label || "",
          pincode: delivery.addresses[0]?.pincode || "",
          country: delivery.addresses[0]?.country?.label || "",
        }));
      });

      return deliveryDetails;
    };

    const dataToSend: OrderPayload = {
      purpose: values.destination.purposeOfTravel,
      end_date: values.destination.dateRange.end_date as string,
      start_date: values.destination.dateRange.start_date as string,
      type_of_order: "buy",
      opportunity_id: location?.state?.opportunityId || 0,
      products: productsWithCustomerInfo(),
      delivery_details: transformDeliveriesToDeliveryDetails(
        values.deliveries as DeliveryFormValues[]
      ),
    };
    await mutateAsync(dataToSend);
    // if(isSuccess){
    //   const orderId= resposne?.data?.order_id
    //   navigate(`/forex/orderdetails/${orderId}`)
    //   form.reset()
    //   setCurrentStep(1)
    //   setIsSubmitting(false)
    //   setIsProductModalOpen(false)
    // }
  };

  const handleCancel = () => {
    nevigate("/forex/orders");
  };

  return (
    <div className="h-screen overflow-hidden mx-auto p-4">
      {(isLoading || loader) && <Spinner />}

      <FormProvider {...form}>
        <form
          className="h-full"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="flex flex-col h-full gap-3">
            {/* <NavbarWithActions header="Create Order" /> */}
            <Navbar>
              <div className=" w-full flex justify-between">
                <div className=" flex gap-3 items-center">
                  <Img
                    src="images/sidebar_img/remit.svg"
                    className="h-5"
                    alt=""
                  />
                  <div className="gap-2 flex flex-col">
                    <span className="text-sm font-semibold">
                      Create Forex Order
                    </span>
                    <span className="text-xs font-normal text-[#999999]">
                      Create a forex transaction using an unitilized deal
                    </span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="gap-2.5 flex items-center">
                    <Img src="images/overview/wrench.svg" alt="" />
                    <span className="text-sm font-semibold text-blue-700">
                      Help
                    </span>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger>
                      <button className="rounded-lg py-[11px] px-4 border bg-gray-button-gray-lite border-gray-border-gray-lite">
                        Cancel Order
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogPortal>
                      <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                      <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                        <AlertDialogHeader className="flex text-left gap-4 pt-8 pb-4 px-10 ">
                          <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#7E7E7E] ">
                            This action cannot be undone. This will permanently
                            delete your progress and remove the actions
                            preformed.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                          <AlertDialogCancel
                            className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                            asChild
                          >
                            <button>Close</button>
                          </AlertDialogCancel>
                          <AlertDialogAction
                            className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                            asChild
                          >
                            <button className="" onClick={handleCancel}>
                              Continue
                            </button>
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialogPortal>
                  </AlertDialog>
                </div>
              </div>
            </Navbar>
            <div className="flex sm:flex-col md:flex-col flex-row gap-3 h-full">
              <div className="bg-white-A700 p-3 rounded-lg w-full h-full">
                <div className="h-full space-y-2">
                  <h3>
                    <Stepper
                      currentStep={currentStep - 1}
                      steps={steps.map((step) => step.title)}
                    />
                  </h3>
                  <section className="">{step.component}</section>
                  <footer className="flex gap-2 pt-4">
                    <>
                      {!isFirstStep && (
                        <Button
                          className="rounded-md border border-blue-700  text-blue-700"
                          type="button"
                          variant="outline"
                          onClick={handleBack}
                        >
                          Back
                        </Button>
                      )}
                    </>
                    <>
                      {/* {isOptionalStep && (
                        <Button className="rounded-md border border-blue-700 bg-blue-700 text-white-A700" type="button" variant="ghost" onClick={handleSkip}>
                          Skip
                        </Button>
                      )} */}
                      {!isLastStep ? (
                        currentStep === 2 &&
                        !form.watch("customers.0.products")?.length ? (
                          <Button
                            className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                            type="button"
                            variant="ghost"
                            onClick={handleNextWithProduct}
                          >
                            Continue to add product
                          </Button>
                        ) : (
                          <Button
                            className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                            type="button"
                            onClick={() => handleNext()}
                          >
                            Next
                          </Button>
                        )
                      ) : (
                        <Button
                          className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                          onClick={() => {
                            const customers = form.getValues().customers
                            const filteredCustomers = customers.filter(c=> c?.fullName || c.customerId)
                            form.setValue("customers", filteredCustomers);
                            form.handleSubmit(handleSubmit)()
                          }}
                          type="button"
                        >
                          {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                      )}
                    </>
                  </footer>
                  {/* <div className="mt-8 p-4 border rounded-md overflow-auto h-60">
                    <h2 className="text-lg font-semibold mb-2">Current Form Data (Debug):</h2>
                    <pre className="text-xs bg-muted p-2 rounded">
                      {JSON.stringify(form.watch(), null, 2)}
                    </pre>
                  </div> */}
                </div>
              </div>
              <div className="bg-white-A700 p-3 rounded-lg w-[45%] sm:w-full md:w-full h-full">
                <CalculationPanel />
              </div>
            </div>
          </div>
        </form>
        {isProductModalOpen && (
          <ProductModal
            config={productsConfig}
            order_type={form.getValues("order_type")}
            isOpen={isProductModalOpen}
            onClose={() => setIsProductModalOpen(false)}
            onSubmit={async (values) => {
              const currentProducts =
                form.getValues("customers.0.products") || [];
              const updatedProducts = [...currentProducts, values];
              form.setValue("customers.0.products", updatedProducts);
              setIsProductModalOpen(false);
            }}
          />
        )}
      </FormProvider>

      {/* Debug panel to show current form values */}
    </div>
  );
}
